import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import cn from "classnames";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import IconChevronDown from "@/icons/icon-chevron-down";
import IconChevronUp from "@/icons/icon-chevron-up";
import IconEdit from "@/icons/icon-edit";
import IconTrash from "@/icons/icon-trash";
import { useCartWithHooks } from "~/hooks/use-cart";
import useLoyaltyRewards, { LOYALTY_REWARDS_QUERY_KEY } from "~/hooks/use-loyalty-rewards";
import useRewardRequirementHint from "~/hooks/use-reward-requirement-hint";
import {
  findAppliedCouponLine,
  findRewardDiscountLineId,
  getRewardCouponCode,
  getRewardCta,
  getRewardLabel,
  isRewardAppliedOnCart,
  isRewardAvailable,
  isRewardPendingOnCart,
} from "~/shared/loyalty/loyalty-rewards.utils";
import { getCouponErrorMessage } from "~/utils/coupon-utils";

import Button from "../common/button";
import Icon from "../common/icon";
import IconDiscount from "../common/icons/icon-discount";
import InputField from "../form/input-field";
import InputRadioCheckbox from "../form/input-radio-checkbox";
import styles from "./cart-coupon-selector.module.scss";
import CartPromoCode from "./cart-promo-code";

const MANUAL = "manual";

export default function CartCouponSelector() {
  const t = useTranslations();
  const queryClient = useQueryClient();
  const { cart, addCouponCode, cancelDiscountOrderLine, couponError, setCouponError } = useCartWithHooks();
  const { data: couponRewards = [] } = useLoyaltyRewards({ onlyAvailable: !0 });
  const requirementHint = useRewardRequirementHint();

  const lines = cart?.ShoppingCart?.Lines ?? [];
  const hasOrder = !!cart?.ShoppingCart?.ID;
  // The cart lists coupon promotions only: gifts and auto-applied rewards have nothing to pick
  // here, they show up in the "my account" list.
  const hasCartProducts = lines.some((line) => line.Product);

  const appliedCouponLine = findAppliedCouponLine(lines);
  const appliedReward = couponRewards.find((reward) => isRewardAppliedOnCart(lines, reward));
  const hasApplied = !!appliedReward || !!appliedCouponLine;

  const appliedLabel = appliedReward ? getRewardLabel(appliedReward) : appliedCouponLine?.DiscountCouponCode;
  const appliedCode = appliedReward ? getRewardCouponCode(appliedReward) : appliedCouponLine?.DiscountCouponCode;

  // Removable only if we can find a cancelable discount line: auto-applied gifts have none -> no "Remove".
  const removableLineId =
    appliedCouponLine?.ID ?? (appliedReward ? findRewardDiscountLineId(lines, appliedReward) : undefined);
  const isRemovable = removableLineId != null;

  const schema = z.object({ code: z.string().min(1, t("error.fieldRequired")) });
  type Fields = z.infer<typeof schema>;
  const form = useForm<Fields>({ resolver: zodResolver(schema) });

  const [selected, setSelected] = useState<string | undefined>(undefined);
  const [expanded, setExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string>();
  const [lastCode, setLastCode] = useState<string>();

  const appliedSelection = appliedReward ? getRewardCouponCode(appliedReward) : appliedCouponLine ? MANUAL : undefined;

  const resetToApplied = () => {
    setSelected(appliedSelection);
    if (appliedCouponLine && !appliedReward) {
      form.setValue("code", appliedCouponLine.DiscountCouponCode ?? "");
    } else {
      form.reset();
    }
  };

  // async errors from AddDiscountCouponToOrder inline, in EVA's own words.
  useEffect(() => {
    if (!couponError) {
      return;
    }
    setFeedback(getCouponErrorMessage(couponError, t, lastCode));
    setCouponError(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponError, lastCode]);

  useEffect(() => {
    resetToApplied();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removableLineId, appliedReward?.BackendID, appliedCouponLine?.DiscountCouponCode]);

  useEffect(() => {
    if (hasApplied) {
      setExpanded(false);
    }
  }, [hasApplied]);

  // Empty cart: no loyalty rewards to pick, fall back to the plain promo code field.
  if (couponRewards.length === 0 || !hasCartProducts) {
    return <CartPromoCode />;
  }

  const manualCode = form.watch("code");
  const selectedCode = selected === MANUAL ? manualCode : selected;
  const isSelectionApplied = !!selectedCode && !!appliedCode && selectedCode === appliedCode;
  const applyDisabled = isSubmitting || !selected || (selected === MANUAL && !manualCode) || isSelectionApplied;

  const removeApplied = () =>
    removableLineId != null
      ? cancelDiscountOrderLine({ payload: { OrderLineID: removableLineId } })
      : Promise.resolve();

  async function onApply() {
    if (isSubmitting || applyDisabled) {
      return;
    }
    setFeedback(undefined);
    const code = selected === MANUAL ? form.getValues("code") : selected;
    if (selected === MANUAL && !(await form.trigger("code"))) {
      return;
    }
    if (!code) {
      return;
    }
    const previousCode = hasApplied ? appliedCode : undefined;
    setLastCode(code);
    setIsSubmitting(true);
    try {
      try {
        await removeApplied();
      } catch {
        // Never apply on top of a coupon we failed to remove.
        setFeedback(t("error.checkout.generic"));
        return;
      }
      let response;
      try {
        response = await addCouponCode({ orderType: 0, payload: { CouponCode: code } });
      } catch (error) {
        // async errors are inline in the couponError effect
        await restorePreviousCoupon(previousCode, code);
        throw error;
      }
      if (response && !response.DiscountIsApplied) {
        setFeedback(t("error.invalid_coupon_combination"));
        await restorePreviousCoupon(previousCode, code);
        return;
      }
      setExpanded(false);
      await queryClient.invalidateQueries({ queryKey: [LOYALTY_REWARDS_QUERY_KEY] });
    } catch {
      // async errors are inline in the couponError effect
    } finally {
      setIsSubmitting(false);
    }
  }

  /** Puts back the coupon we removed for a swap that then failed. */
  async function restorePreviousCoupon(previousCode: string | undefined, failedCode: string) {
    if (!previousCode || previousCode === failedCode) {
      return;
    }
    try {
      await addCouponCode({ orderType: 0, payload: { CouponCode: previousCode } });
    } catch {
      // Nothing left to do: the cart stays without a coupon and the feedback is already shown.
    }
  }

  function onCancel() {
    if (isSubmitting) {
      return;
    }
    setFeedback(undefined);
    if (hasApplied) {
      resetToApplied();
      setExpanded(false);
      return;
    }
    setSelected(undefined);
    setExpanded(false);
    form.reset();
  }

  async function onRemove() {
    if (isSubmitting || !isRemovable) {
      return;
    }
    setFeedback(undefined);
    setIsSubmitting(true);
    try {
      await removeApplied();
      setSelected(undefined);
      form.reset();
      await queryClient.invalidateQueries({ queryKey: [LOYALTY_REWARDS_QUERY_KEY] });
    } catch {
      setFeedback(t("error.checkout.generic"));
    } finally {
      setIsSubmitting(false);
    }
  }
  return (
    <div className={styles.container}>
      <button type="button" className={styles.header} onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
        <span className={styles.headerTitle}>
          <IconDiscount width={24} height={24} />
          <p className={styles.couponTitle}>{t("generic.coupon")}</p>
          <span className={styles.count}>{couponRewards.length}</span>
        </span>
        {hasApplied ? (
          <span className={styles.edit}>
            {t("generic.edit")}
            <Icon icon={IconEdit} aria-hidden width={16} height={16} />
          </span>
        ) : (
          <Icon icon={expanded ? IconChevronUp : IconChevronDown} aria-hidden width={16} height={16} />
        )}
      </button>

      {hasApplied && !expanded && (
        <div className={styles.summary}>
          <span className={styles.summaryLabel}>{appliedLabel}</span>
          {isRemovable && (
            <button type="button" className={styles.remove} disabled={isSubmitting} onClick={onRemove}>
              {t("generic.remove")}
              <Icon icon={IconTrash} aria-hidden width={16} height={16} />
            </button>
          )}
        </div>
      )}

      <div className={cn(styles.panel, expanded && styles.panelOpen)}>
        <div className={styles.panelInner}>
          {/* TODO: Remove expanded for accordion closing animation, but need to fix radio button "checked" issue */}
          {expanded && (
            <>
              <ul className={styles.list}>
                {couponRewards.map((reward, index) => {
                  const code = getRewardCouponCode(reward);
                  const cta = getRewardCta({
                    applied: isRewardAppliedOnCart(lines, reward),
                    pending: isRewardPendingOnCart(lines, reward, hasOrder),
                    hasCoupons: true,
                    available: isRewardAvailable(reward),
                    hasCartProducts,
                    hasAppliedCoupon: false,
                  });
                  const isSelectable = cta === "apply" || cta === "applied";
                  const hint =
                    cta === "pending" || cta === "disabled"
                      ? requirementHint(reward) ??
                        (cta === "pending" ? t("generic.myaccount.dashboard.rewardsPendingHint") : undefined)
                      : undefined;

                  return (
                    <li key={`${reward.BackendID}-${index}`}>
                      <InputRadioCheckbox
                        type="radio"
                        name="couponChoice"
                        value={code}
                        checked={selected === code}
                        disabled={!isSelectable || isSubmitting || !code}
                        onChange={() => code && setSelected(code)}
                        className={cn(styles.option, !isSelectable && styles.optionDisabled)}
                      >
                        <span className={styles.optionLabel}>{getRewardLabel(reward)}</span>
                        {hint && <span className={styles.optionHint}>{hint}</span>}
                      </InputRadioCheckbox>
                    </li>
                  );
                })}

                <li>
                  <InputRadioCheckbox
                    type="radio"
                    name="couponChoice"
                    value={MANUAL}
                    checked={selected === MANUAL}
                    disabled={isSubmitting}
                    onChange={() => setSelected(MANUAL)}
                    className={cn(styles.option, styles.optionManual)}
                  >
                    <span className={styles.optionLabel}>{t("generic.cart.addACouponCode")}</span>
                  </InputRadioCheckbox>
                  <div>
                    <InputField
                      label={t("generic.promoCode")}
                      disabled={isSubmitting}
                      disableButton
                      {...form.register("code")}
                      onClick={() => setSelected(MANUAL)}
                      hideErrorIcon
                      error={form.formState.errors.code?.message}
                      successMessage={selected === MANUAL && isSelectionApplied ? t("generic.codeApplied") : undefined}
                    />
                  </div>
                </li>
              </ul>
              <p className={styles.feedback}>{feedback}</p>
              <div className={styles.actions}>
                <Button variant="secondary" disabled={isSubmitting} onClick={onCancel}>
                  {t("generic.cancel")}
                </Button>
                <Button variant="primary" loading={isSubmitting} disabled={applyDisabled} onClick={onApply}>
                  {t("generic.apply")}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
